import React, { ReactNode } from 'react';
import LoadingSpinner from 'components/LoadingSpinner';
import EmptyTableState from 'components/EmptyTableState';
import { OperationType } from 'screens/Applications&WorkOrders/RouteAdmin';
import { Button, ButtonType } from 'syngenta-digital-cropwise-react-ui-kit';
import FilterIcon from 'assets/filter_outlined_white.svg?react';
import { useSummaryParams } from 'screens/Applications&WorkOrders/Context/SummaryParamsContext';
import { initialValues as defaultFilterValues } from 'screens/Applications&WorkOrders/Components/FilterDrawer/Constants/filterFormikData';
import DetailPanelEmptyState from './DetailPanelEmptyState';
import {
  ADJUST_FILTERS_LABEL,
  EMPTY_STATE_CONTENT,
  CLEAR_FILTERS_LABEL
} from '../Constants';

export interface DetailPanelProps {
  title: string;
  actionMenu?: ReactNode;
  children: ReactNode;
  isLoading?: boolean;
  isEmpty?: boolean;
  testId?: string;
  showSearchEmptyState?: boolean;
  showFilterEmptyState?: boolean;
  operationType: OperationType;
}

const DetailPanel = ({
  title,
  actionMenu,
  children,
  isLoading = false,
  isEmpty = false,
  showSearchEmptyState = false,
  showFilterEmptyState = false,
  testId = 'detail-panel',
  operationType
}: Readonly<DetailPanelProps>) => {
  const { setIsDrawerVisible, handleFiltersChange } = useSummaryParams();

  if (isLoading) {
    return (
      <div
        data-testid={testId}
        className="application-detail-panel flex-1 min-w-0 bg-neutral-10 flex justify-center items-center"
      >
        <LoadingSpinner className="flex items-center justify-center h-19 w-60 py-8 px-4 rounded-lg bg-white shadow-md" />
      </div>
    );
  }

  if (showSearchEmptyState) {
    return (
      <DetailPanelEmptyState
        title={EMPTY_STATE_CONTENT[operationType].search.title}
        content={EMPTY_STATE_CONTENT[operationType].search.content}
      />
    );
  }

  if (showFilterEmptyState) {
    return (
      <DetailPanelEmptyState
        title={EMPTY_STATE_CONTENT[operationType].filter.title}
        content={EMPTY_STATE_CONTENT[operationType].filter.content}
        actions={
          <div className="flex items-center gap-2 mt-4">
            <Button
              type={ButtonType.primary}
              icon={<FilterIcon />}
              onClick={() => setIsDrawerVisible(true)}
              size="large"
              className="text-sm"
            >
              {ADJUST_FILTERS_LABEL}
            </Button>
            <Button
              type={ButtonType.outline}
              onClick={() => handleFiltersChange(defaultFilterValues)}
              size="large"
              className="text-sm"
            >
              {CLEAR_FILTERS_LABEL}
            </Button>
          </div>
        }
      />
    );
  }

  if (isEmpty) {
    return <EmptyTableState />;
  }

  return (
    <div
      data-testid={testId}
      className="application-detail-panel flex flex-col w-full bg-neutral-10 overflow-hidden"
      style={{ minHeight: 'calc(100vh - 63px)' }}
    >
      <header className="flex items-center justify-between bg-white pb-15px p-4 border-b border-color-neutral-20">
        <h2
          className="text-2xl leading-8 font-semibold text-neutral-1000 truncate"
          title={title}
        >
          {title}
        </h2>
        {actionMenu}
      </header>
      <div className="py-4 px-17px flex flex-col flex-1">{children}</div>
    </div>
  );
};

export default DetailPanel;
